import React from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import filterFactory, { textFilter } from "react-bootstrap-table2-filter";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-filter/dist/react-bootstrap-table2-filter.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import PropTypes from "prop-types";

/**
 * Table component renders a table with customizable features such as sorting, searching, and pagination.
 * @param {Object} props - Props for the Table component.
 * @param {Array} props.data - Data to be displayed in the table.
 * @param {boolean} props.sort - Flag indicating if sorting is enabled.
 * @param {boolean} props.search - Flag indicating if searching is enabled.
 * @param {boolean} props.pagination - Flag indicating if pagination is enabled.
 * @returns {JSX.Element} - Rendered component.
 */
const Table = ({ data, sort, search, pagination }) => {
  const selectedService = localStorage.getItem("selectedService");
if (selectedService === "clinical") {
  data = data.map((booking) => ({
    ...booking,
    testType: "Thrive Panel",
    sampleType: "Venous Blood",
    reportedDate: booking.reportedDate === null ? "N/A" : booking.reportedDate,
  }));
}
  /* Custom filter function for the status column */
  const statusFilter = (filterVal, data) => {
    if (!filterVal) return data;

    return data.filter((row) =>
      row.status.component?.props?.label
        ?.toLowerCase()
        .includes(filterVal.toLowerCase()),
    );
  };

  // Column definitions with common configuration
  const createColumn = (dataField, text, customFilter = null) => ({
    dataField,
    text,
    sort,
    filter: search && (customFilter || textFilter({ placeholder: text })),
    headerClasses:
      dataField !== "reportedDate" &&
      dataField !== "doctor" &&
      dataField !== "status"
        ? "column-heading-name"
        : "",
  });

  /* Table columns configuration */
  const commonColumns = [
    createColumn("testType", "Test type"),
    createColumn("sampleType", "Sample type"),
    createColumn("reportedDate", "Reported date"),
    {
      ...createColumn(
        "status",
        "Status",
        textFilter({
          placeholder: "Status",
          onFilter: statusFilter,
        }),
      ),
      formatter: (cell, row) => row.status.component,
    },
  ];
  const columns = commonColumns;

  /* Table pagination options */
  const paginationOptions = pagination
    ? {
        sizePerPage: 4,
        pageStartIndex: 1,
        paginationSize: 1,
        paginationTotalRenderer: () => null,
        prePageText: "<",
        nextPageText: ">",
        totalSize: data?.length,
        showTotal: true,
        alwaysShowAllBtns: true,
        withFirstAndLast: false,
        hideSizePerPage: true,
      }
    : null;

  /* Row classes based on booking status */
  const rowClasses = (row) => {
    if (row?.bookingStatus && row?.bookingStatus?.label === "Registered") {
      return "registered-text";
    }
    return "";
  };

  /* Render the Bootstrap table component with provided data, columns, filters, and pagination */
  return (
    <BootstrapTable
      columns={columns}
      data={data}
      filter={search ? filterFactory() : undefined}
      keyField="id"
      pagination={pagination ? paginationFactory(paginationOptions) : undefined}
      rowClasses={rowClasses}
    />
  );
};

Table.propTypes = {
  data: PropTypes.array.isRequired,
  sort: PropTypes.bool,
  search: PropTypes.bool,
  pagination: PropTypes.bool,
};

export default Table;
